<template>
  <AuthLayout :max-width="480">
    <template v-if="tokenError">
      <v-icon color="error" size="56" class="mb-4">mdi-alert-circle</v-icon>
      <v-card-title class="justify-center mb-2">Érvénytelen link</v-card-title>
      <v-card-text
        >A meghívó link érvénytelen vagy már felhasználták.</v-card-text
      >
    </template>

    <template v-else>
      <v-card-title class="mb-4">Regisztráció</v-card-title>

      <v-form ref="formRef" @submit.prevent="submit">
        <v-card-text>
          <v-text-field
            v-model="email"
            label="Email cím"
            readonly
            disabled
            class="mb-2"
          />
          <v-text-field
            v-model="form.lastName"
            label="Vezetéknév"
            class="mb-2"
            :rules="[rules.required]"
          />
          <v-text-field
            v-model="form.firstName"
            label="Keresztnév"
            class="mb-2"
            :rules="[rules.required]"
          />
          <v-text-field
            v-model="form.password"
            label="Jelszó"
            type="password"
            class="mb-2"
            :rules="[rules.required, rules.minPassword]"
          />
          <v-text-field
            v-model="form.passwordConfirm"
            label="Jelszó megerősítése"
            type="password"
            :rules="[rules.required, rules.passwordMatch]"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn type="submit" color="primary" :loading="submitting"
            >Regisztráció</v-btn
          >
        </v-card-actions>
      </v-form>
    </template>
  </AuthLayout>
</template>

<script setup lang="ts">
import AuthLayout from "@/components/layout/AuthLayout.vue";
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { CompanyUserService } from "@/services/CompanyUserService";
import { useToast } from "@/services/useToast";

const route = useRoute();
const router = useRouter();
const companyUserService = new CompanyUserService();
const { showToast } = useToast();

const token = ref("");
const email = ref("");
const tokenError = ref(false);
const submitting = ref(false);
const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(
  null
);

const rules = {
  required: (v: string) => !!v?.trim() || "Kötelező mező",
  minPassword: (v: string) =>
    v?.length >= 8 || "A jelszónak legalább 8 karakter hosszúnak kell lennie",
  passwordMatch: (v: string) =>
    v === form.value.password || "A jelszavak nem egyeznek",
};

const form = ref({
  firstName: "",
  lastName: "",
  password: "",
  passwordConfirm: "",
});

onMounted(async () => {
  token.value = route.query.token as string;
  if (!token.value) {
    tokenError.value = true;
    return;
  }
  try {
    const result = await companyUserService.validateToken(token.value);
    email.value = result.email;
  } catch {
    tokenError.value = true;
  }
});

async function submit() {
  const { valid } = await formRef.value!.validate();
  if (!valid) return;

  submitting.value = true;
  try {
    await companyUserService.registerUser({
      token: token.value,
      firstName: form.value.firstName,
      lastName: form.value.lastName,
      password: form.value.password,
    });
    showToast("Sikeres regisztráció");
    router.push("/login");
  } catch (e) {
    if (e?.response?.status === 409) {
      showToast("Ez az email cím már regisztrálva van a rendszerben", "error");
    } else {
      showToast("Regisztráció sikertelen", "error");
    }
  } finally {
    submitting.value = false;
  }
}
</script>
